import { useState, useEffect } from "react";
import { Breadcrumbs, Anchor, Button, Modal, Stack, Group, Text, Loader, Center } from "@mantine/core";
import { UsersTable } from "./users-table";
import { UserForm } from "./user-form";
import { IconPlus, IconAlertTriangle } from "@tabler/icons-react";
import { useUserStore } from "../stores/userStore";

export function UsersPage() {
  const { token, user } = useUserStore();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  }, [token]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to fetch users");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setError(null);
    setIsDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setError(null);
    setIsDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    setUserToDelete(userToDelete);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setDeletingId(userToDelete.id);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/${userToDelete.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }

      await fetchUsers();
      setDeleteModalOpen(false);
      setUserToDelete(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      setError("Failed to delete user");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmitUser = async (formData) => {
    setIsSaving(true);
    setError(null);

    try {
      const url = selectedUser
        ? `${process.env.REACT_APP_API_URL}/api/users/${selectedUser.id}`
        : `${process.env.REACT_APP_API_URL}/api/users`;

      const method = selectedUser ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to save user");
      }

      await fetchUsers();
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving user:", error);
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack h="100%">
      <Group justify="space-between" mb="md">
          <Breadcrumbs>
            <Anchor href="/dashboard">Dashboard</Anchor>
            <Text>Users</Text>
          </Breadcrumbs>
          <Button leftSection={<IconPlus size={16} />} onClick={handleAddUser}>
              Add User
          </Button>
      </Group>

      {isLoading ? (
        <Center h={200}>
            <Loader color="cyan" />
        </Center>
      ) : (
        <>
            <UsersTable
            users={users}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            deletingId={deletingId}
            currentUserId={user?.id}
            />
            {users.length === 0 && (
                <Text ta="center" c="dimmed" py="xl">No users found.</Text>
            )}
        </>
      )}

      <Modal
        opened={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={selectedUser ? "Edit User" : "Add New User"}
        centered
      >
          <Text size="sm" c="dimmed" mb="md">
            {selectedUser
                ? "Update user information and permissions."
                : "Create a new user account."}
          </Text>
          <UserForm
            user={selectedUser}
            onSubmit={handleSubmitUser}
            onCancel={() => setIsDialogOpen(false)}
            isSaving={isSaving}
            error={error}
            currentUserId={user?.id}
          />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        title={
          <Group gap="xs">
            <IconAlertTriangle size={24} color="var(--mantine-color-red-6)" />
            <Text fw={600}>Delete User</Text>
          </Group>
        }
        centered
      >
        <Stack>
          <Text>
            Are you sure you want to delete <Text component="span" fw={600}>{userToDelete?.name}</Text> ({userToDelete?.email})?
          </Text>
          <Text size="sm" c="dimmed">
            This action cannot be undone. All data associated with this user will be permanently deleted.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpen(false);
                setUserToDelete(null);
              }}
              disabled={deletingId === userToDelete?.id}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={confirmDeleteUser}
              loading={deletingId === userToDelete?.id}
            >
              Delete User
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
